import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  PlusIcon,
} from '@heroicons/react/24/outline';
import DropdownMenu from '../ui/DropdownMenu';
import { Button } from '../ui/Button';
import StatusPill from '../ui/StatusPill';
import useTableSort from '../ui/useTableSort';
import { humanizeEnum } from '../../utils/humanize';
import Checkbox from '../ui/Checkbox';
import { useConfirm } from '../ui';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../ui/Table';

import { Alert } from '../ui/Alert';
import { Card } from '../ui/Card';
import EmptyState from '../ui/EmptyState';
import { SalespeopleChips } from '../ui/SalespeopleChips';
import { normaliseSalespeople } from '../../utils/normaliseSalespeople';
import PageHeader from '../ui/PageHeader';
import ListShell from '../ui/ListShell';
import BulkActionBar from '../ui/BulkActionBar';
import { customerAPI, settingsAPI } from '../../services/api';
import AddCustomerDrawer from './AddCustomerDrawer';
import { formatDate } from '../../utils/formatDate';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../ui/Dialog';
import SelectMenu from '../ui/SelectMenu';
import useSalespersonOptions from '../../hooks/useSalespersonOptions';
import { useToast } from '../ui/Toast';

const STATUS_FILTERS = ['All', 'Active', 'Lead', 'Inactive'];
const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

// Latest activity timestamp (ms) across quote/job/invoice/updated dates, or
// null. Shared by the Last Activity cell and its IDI-479 sort.
function lastActivityTime(customer) {
  const raw = [
    customer.last_quote_date || customer.lastQuoteDate,
    customer.last_job_date || customer.lastJobDate,
    customer.last_invoice_date || customer.lastInvoiceDate,
    customer.updated_at || customer.updatedAt,
  ];
  const timestamps = raw
    .filter(d => d && typeof d === 'string' && d.length > 4)
    .map(d => new Date(d).getTime())
    .filter(t => !isNaN(t) && t > 0);
  if (timestamps.length === 0) return null;
  return Math.max(...timestamps);
}

// Map status for display
function customerDisplayStatus(customer) {
  const s = (customer.status || customer.lead_status || 'lead').toLowerCase();
  if (s === 'new' || s === 'lead') return 'lead';
  if (s === 'converted' || s === 'active') return 'active';
  if (s === 'inactive') return 'inactive';
  return s;
}

// IDI-479: sortable columns. "Off" keeps the PT-33.7 name-ascending order.
const CUSTOMER_SORT_COLUMNS = [
  { key: 'name', type: 'text', value: (c) => c.name || null },
  { key: 'type', type: 'text', value: (c) => humanizeEnum(c.customer_type || c.type) || null },
  { key: 'salesperson', type: 'text', value: (c) => normaliseSalespeople(c)[0]?.name || null },
  { key: 'status', type: 'text', value: (c) => humanizeEnum(customerDisplayStatus(c)) || null },
  { key: 'lastActivity', type: 'date', value: (c) => lastActivityTime(c) },
];

// IDI-316 row 3: bulk Re-tag Type / Re-assign Salesperson picker dialog.
// Same pattern as JobsPage.js's ReassignPickerDialog — that component is
// local/unexported there, so this is a Customers-scoped twin rather than a
// shared import (mirrors the existing precedent of two near-identical
// customer forms/drawers already in this codebase; consolidating both
// reassign dialogs into one shared component is a separate, larger refactor
// not attempted here).
function CustomerReassignDialog({
  mode,
  value,
  onValueChange,
  busy,
  onClose,
  onSubmit,
  selectedCount,
  customerTypeOptions,
  salespersonOptions,
}) {
  const isType = mode === 'type';
  const isSalesperson = mode === 'salesperson';
  const open = isType || isSalesperson;

  const title = isType ? 'Re-tag customer type' : isSalesperson ? 'Re-assign salesperson' : '';
  const label = isType ? 'New customer type' : 'New salesperson';
  const options = isType ? customerTypeOptions : isSalesperson ? salespersonOptions : [];

  return (
    <Dialog open={open} onClose={busy ? () => {} : onClose} size="sm">
      <DialogHeader onClose={busy ? null : onClose}>{title}</DialogHeader>
      <DialogBody>
        <p className="text-sm mb-3" style={{ color: 'var(--color-text-muted)' }}>
          Applies to {selectedCount} selected customer{selectedCount === 1 ? '' : 's'}.
        </p>
        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>
          {label}
        </label>
        <SelectMenu
          value={value}
          onChange={onValueChange}
          options={options}
          placeholder={options.length === 0 ? 'No options available' : `Select — ${options.length} available`}
          disabled={busy || options.length === 0}
        />
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onSubmit} disabled={busy || !value}>
          {busy ? 'Updating…' : `Reassign ${selectedCount}`}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

export default function CustomersPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { options: salespersonOptions } = useSalespersonOptions();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  // PORTICA-56: true while more customer batches are still streaming in
  // behind an already-rendered first page. Same pattern as
  // QuotesPage.js/JobsPage.js (IDI-56/PORTICA-56).
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');
  // Iter-18: typeFilter removed — no customer-type chips on landing.
  // Iter-18: customer-type chips removed from landing (only status filters
  // per CU-1 wire). Keep the list available for the AddCustomerDrawer type SelectMenu.
  const [customerTypes, setCustomerTypes] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [, setBulkMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Iter-18: Dashboard '+ Create > New Customer' navigates to /customers?add=true
  // — auto-open the AddCustomerDrawer inline (per DASH-1 wire; no /customers/add page).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('add') === 'true') {
      setDrawerOpen(true);
      // clean the URL so refresh doesn't re-open
      searchParams.delete('add');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const searchTimerRef = useRef(null);
  const bulkMenuRef = useRef(null);
  const confirm = useConfirm();

  // Load customer types from settings
  useEffect(() => {
    settingsAPI.getList('customer_types').then(res => {
      setCustomerTypes(Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : []);
    }).catch(() => { setCustomerTypes([]); });
  }, []);

  // Debounce search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchTerm]);

  // PORTICA-56: PROGRESSIVE loading (607 rows / 349 KB today, "not yet at
  // the cliff" but on the same unbounded-list trajectory as Quotes/Jobs).
  // CustomersPage has no chip-count-from-full-dataset invariant to preserve
  // (unlike QuotesPage/JobsPage), so this is the simpler half of the
  // pattern: fetch in batches, merge into the same `customers` state,
  // nothing else changes.
  const loadSeqRef = useRef(0);
  const CUSTOMERS_BATCH_SIZE = 500;

  const loadCustomers = useCallback(async () => {
    const mySeq = ++loadSeqRef.current;
    let first = true;
    try {
      setLoading(true);
      setBackgroundLoading(false);
      setCustomers([]);

      let cursor;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (loadSeqRef.current !== mySeq) return;
        const response = await customerAPI.getCustomers({ limit: CUSTOMERS_BATCH_SIZE, cursor });
        const body = response?.data || response || {};
        const batch = Array.isArray(body) ? body : (body.customers || []);
        if (loadSeqRef.current !== mySeq) return;

        const isFirstBatch = first;
        setCustomers((prev) => (isFirstBatch ? batch : [...prev, ...batch]));
        if (isFirstBatch) {
          setLoading(false);
          first = false;
        }

        const hasMore = Array.isArray(body) ? false : !!body.has_more;
        cursor = Array.isArray(body) ? undefined : body.next_cursor;
        if (!hasMore || !cursor) break;
        setBackgroundLoading(true);
      }
    } catch (err) {
      if (loadSeqRef.current !== mySeq) return;
      if (first) {
        setError('Unable to load customers.');
        setCustomers([]);
      } else {
        setError('Some customers may be missing — retry to load the rest.');
      }
      setLoading(false);
    } finally {
      if (loadSeqRef.current === mySeq) {
        setLoading(false);
        setBackgroundLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // Auto-refresh every 30s (NO refresh button per spec)
  useEffect(() => {
    const interval = setInterval(() => {
      loadCustomers();
    }, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [loadCustomers]);

  // ─── Lifecycle actions (REQ-WF-06) ─────────────────────────────────────────
  const handleInactivate = async (customer) => {
    const ok = await confirm({
      title: 'Inactivate Customer?',
      message: `Inactivate ${customer.name || customer.first_name}? They won't appear in active lists.`,
      confirmLabel: 'Inactivate',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await customerAPI.updateCustomer(customer.id, { status: 'inactive' });
      loadCustomers();
    } catch (err) { setError('Failed to inactivate customer'); }
  };

  const handleReactivate = async (customer) => {
    try {
      await customerAPI.updateCustomer(customer.id, { status: 'active' });
      loadCustomers();
    } catch (err) { setError('Failed to reactivate customer'); }
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setDrawerOpen(true);
  };

  // Close bulk menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target)) {
        setBulkMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Filter customers
  const filtered = useMemo(() => {
    return customers.filter(c => {
      // Status filter
      const customerStatus = (c.status || c.lead_status || 'lead').toLowerCase();
      if (statusFilter && statusFilter !== 'All') {
        const filterLower = statusFilter.toLowerCase();
        if (filterLower === 'lead' && customerStatus !== 'lead' && customerStatus !== 'new') return false;
        if (filterLower === 'active' && customerStatus !== 'active' && customerStatus !== 'converted') return false;
        if (filterLower === 'inactive' && customerStatus !== 'inactive') return false;
      }


      // Search
      if (debouncedSearch) {
        const term = debouncedSearch.toLowerCase();
        const matchesName = (c.name || '').toLowerCase().includes(term);
        const matchesEmail = (c.email || '').toLowerCase().includes(term);
        const matchesPhone = (c.phone || '').toLowerCase().includes(term);
        if (!matchesName && !matchesEmail && !matchesPhone) return false;
      }

      return true;
    });
  }, [customers, statusFilter, debouncedSearch]);

  // PT-33.7 (2026-07-27): default sort by name ascending. Client-side sort
  // on the filtered set. When the backend supports `orderBy` params, this
  // should move server-side to avoid sorting a truncated page — flagged as
  // a pagination concern in the report. For now the full customer list is
  // loaded in one fetch (no server pagination), so client-side is correct.
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
    });
  }, [filtered]);

  // IDI-479: header sort over all loaded rows, before the slice.
  const { sortedRows: sortedCustomers, getSortProps } = useTableSort(sorted, CUSTOMER_SORT_COLUMNS, {
    onSortChange: () => setPage(1),
  });

  // Pagination
  const totalPages = Math.ceil(sorted.length / pageSize);
  const paginatedCustomers = sortedCustomers.slice((page - 1) * pageSize, page * pageSize);

  // Selection
  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === paginatedCustomers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(paginatedCustomers.map(c => c.id));
    }
  };

  // Bulk actions
  // IDI-283: Re-tag Type / Re-assign Salesperson / Archive were silent
  // console.log no-ops on a fully reachable surface (bar appears the moment
  // a row checkbox is ticked). Per the ticket's ruling, these are honest-
  // disabled (not implemented, not deleted — Merge was deleted separately
  // under IDI-266). Do NOT wire real bulk-action behavior here — that is
  // separate feature work; this ticket is about honesty only.

  // IDI-316 row 3 (2026-09-12): Re-tag Type / Re-assign Salesperson are now
  // wired — the "separate feature work" IDI-283 deferred. Archive stays
  // honest-disabled (no backend field/endpoint confirmed for a customer
  // archive/soft-delete state; wiring it would be a fake unlock).
  const [reassignMode, setReassignMode] = useState(null); // 'type' | 'salesperson' | null
  const [reassignValue, setReassignValue] = useState('');
  const [reassignBusy, setReassignBusy] = useState(false);

  const closeReassign = () => {
    if (reassignBusy) return;
    setReassignMode(null);
    setReassignValue('');
  };

  const submitReassign = async () => {
    if (!reassignValue || reassignBusy || selectedIds.length === 0) return;
    setReassignBusy(true);
    // IDI-138 precedent (AddCustomerDrawer.js/CustomerFormPanel.js): the
    // stored shape for salesperson is {id, name} pairs, not a bare id —
    // storing the bare id would leave the record with no display name if
    // that person is later removed from the picker's options.
    const salespersonOpt = salespersonOptions.find((o) => o.value === reassignValue);
    const payload = reassignMode === 'type'
      ? { customer_type: reassignValue }
      : {
          salesperson: { id: reassignValue, name: salespersonOpt?.label || reassignValue },
          salespersons: [{ id: reassignValue, name: salespersonOpt?.label || reassignValue }],
        };
    let succeeded = 0;
    let failed = 0;
    // No bulk endpoint exists (customerAPI.updateCustomer is per-id, same
    // constraint JobsPage.js's runBulkUpdate documents for jobAPI) — N
    // sequential PUTs, same pattern.
    for (const id of selectedIds) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await customerAPI.updateCustomer(id, payload);
        succeeded += 1;
      } catch (err) {
        failed += 1;
      }
    }
    setReassignBusy(false);
    setReassignMode(null);
    setReassignValue('');
    setSelectedIds([]);
    if (failed === 0) {
      toast.success(`${succeeded} customer${succeeded === 1 ? '' : 's'} updated.`);
    } else {
      toast.error(`${succeeded} updated, ${failed} failed.`);
    }
    loadCustomers();
  };

  // Get last activity date
  // IDI-139 §3: guard against falsy/zero/unparseable values that would render
  // as Unix epoch (12/31/1969). Only valid ISO strings produce dates.
  const getLastActivity = (customer) => {
    const latest = lastActivityTime(customer);
    if (latest == null) return '—';
    return formatDate(new Date(latest));
  };

  // Map status for display
  const getDisplayStatus = customerDisplayStatus;


  // Build chips for ListShell
  // Iter-18: landing chips are STATUS ONLY (All / Active / Lead / Inactive)
  // per CU-1 wire. Custom customer types belong in Settings, not as landing filters.
  const listChips = STATUS_FILTERS.map(status => {
    // Iter18 Customers polish: add count badges to match Quotes page pattern.
    const count = status === 'All'
      ? customers.length
      : customers.filter(c => {
          const s = (c.status || 'active').toLowerCase();
          const f = status.toLowerCase();
          if (f === 'lead') return s === 'lead' || s === 'new';
          if (f === 'active') return s === 'active' || s === 'converted';
          if (f === 'inactive') return s === 'inactive';
          return true;
        }).length;
    return {
      label: status,
      active: statusFilter === status,
      count,
      onClick: () => { setStatusFilter(status === 'All' ? 'All' : (status === statusFilter ? 'All' : status)); setPage(1); },
    };
  });

  return (
    <div className="px-6">
      {/* Header */}
      <PageHeader
        title="Customers"
        actions={
          <Button onClick={() => setDrawerOpen(true)}>
            <PlusIcon className="h-4 w-4 mr-1.5" />
            Add Customer
          </Button>
        }
      />

      {/* Search + filter toolbar */}
      <ListShell
        search={searchTerm}
        onSearchChange={setSearchTerm}
        placeholder="Search by name, email, or phone…"
        chips={listChips}
      >

      {/* Bulk actions bar */}
      {/* IDI-316 row 3 (2026-09-12): Re-tag Type / Re-assign Salesperson wired
          to CustomerReassignDialog + submitReassign above. Archive stays
          honest-disabled per IDI-283's original ruling — no confirmed
          backend archive/soft-delete field or endpoint for customers. */}
      <BulkActionBar
        count={selectedIds.length}
        onClear={() => setSelectedIds([])}
        actions={[
          { label: 'Re-tag Type', onClick: () => { setReassignMode('type'); setReassignValue(''); } },
          { label: 'Re-assign Salesperson', onClick: () => { setReassignMode('salesperson'); setReassignValue(''); } },
          { label: 'Archive', disabled: true, title: 'Bulk archive not yet available' },
        ]}
      />

      <CustomerReassignDialog
        mode={reassignMode}
        value={reassignValue}
        onValueChange={setReassignValue}
        busy={reassignBusy}
        onClose={closeReassign}
        onSubmit={submitReassign}
        selectedCount={selectedIds.length}
        customerTypeOptions={customerTypes.map((t) => ({ value: t.name || t.value, label: t.label || t.name }))}
        salespersonOptions={salespersonOptions}
      />

      {error && <Alert variant="error" className="mb-4" onClose={() => setError(null)}>{error}</Alert>}

      {/* PORTICA-56: informational only — more customer batches are still
          loading in the background behind the rows already shown below. */}
      {backgroundLoading && (
        <p className="text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>
          Loading more customers…
        </p>
      )}

      {/* Table */}
      {loading && customers.length === 0 ? (
        <div className="flex justify-center py-12"><div className="h-32 rounded animate-pulse" style={{backgroundColor:"var(--neutral-100)"}} /></div>
      ) : paginatedCustomers.length === 0 ? (
        /* Empty state — uses canonical EmptyState component (REQ-SYS-EMPTY-01) */
        <EmptyState
          mode={customers.length === 0 ? 'first-run' : 'filtered'}
          title={customers.length === 0 ? 'No customers yet' : 'No customers match your filters'}
          description={customers.length === 0
            ? 'Add your first customer to get started.'
            : 'Try adjusting your search or filters.'}
          action={
            <Button onClick={() => setDrawerOpen(true)}>
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Add Customer
            </Button>
          }
          onClearFilters={() => { setSearchTerm(''); setStatusFilter('All'); setPage(1); }}
        />
      ) : (
        <>
          <Card className="overflow-hidden">
            <Table bordered={false}>
              <TableHead>
                <TableRow>
                  <TableHeader className="w-10">
                    <Checkbox
                      checked={selectedIds.length === paginatedCustomers.length && paginatedCustomers.length > 0}
                      onChange={toggleSelectAll}
                      aria-label="Select all customers"
                    />
                  </TableHeader>
                  <TableHeader {...getSortProps('name')}>Name</TableHeader>
                  <TableHeader {...getSortProps('type')}>Type</TableHeader>
                  <TableHeader {...getSortProps('salesperson')}>Salesperson</TableHeader>
                  <TableHeader {...getSortProps('status')}>Status</TableHeader>
                  <TableHeader {...getSortProps('lastActivity')}>Last Activity</TableHeader>
                  <TableHeader className="w-10"></TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCustomers.map((customer) => (
                  <TableRow
                    key={customer.id}
                    onClick={() => navigate(`/customers/${customer.id}`)}
                    className="cursor-pointer"
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.includes(customer.id)}
                        onChange={() => toggleSelect(customer.id)}
                        stopPropagation
                        aria-label={`Select ${customer.name}`}
                      />
                    </TableCell>
                    <TableCell className="font-semibold text-gray-900">
                      <Link to={`/customers/${customer.id}`} className="hover:underline">
                        {customer.name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {humanizeEnum(customer.customer_type || customer.type) || '—'}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {(() => {
                        const norm = normaliseSalespeople(customer);
                        if (norm.length === 0) return "—";
                        return <SalespeopleChips salespeople={norm} interactive={false} />;
                      })()}
                    </TableCell>
                    <TableCell>
                      <StatusPill status={getDisplayStatus(customer)} />
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {getLastActivity(customer)}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {/* PT-39a: hand-rolled Menu block retired for the DropdownMenu
                          atom. Row-context menu — stopPropagation prevents the row
                          click navigating to the customer detail page when the
                          user is aiming for a menu item. */}
                      <DropdownMenu
                        ariaLabel={`Actions for ${customer.name}`}
                        stopPropagation
                        items={[
                          { label: 'Edit', onClick: () => handleEditCustomer(customer) },
                          customer.status !== 'inactive'
                            ? { label: 'Inactivate', variant: 'danger', onClick: () => handleInactivate(customer) }
                            : { label: 'Reactivate', onClick: () => handleReactivate(customer), className: 'text-green-600' },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">
                Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of {filtered.length}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add Customer Drawer */}
      <AddCustomerDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditingCustomer(null); }}
        onSaved={() => {
          setDrawerOpen(false);
          setEditingCustomer(null);
          loadCustomers();
        }}
        customerTypes={customerTypes}
        editCustomer={editingCustomer}
      />

      </ListShell>
    </div>
  );
}
